import { TXProvider } from '@transifex/react';
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import { getTransifexLocale, useSystemLocale } from '#modules/localization';

import { configureAssessmentTxToken, assessmentTx } from '../i18n/assessmentTx';
import type {
  ResolvedDynamicAssessmentAnalytics,
  DynamicAssessmentAnalytics,
} from '../types/dynamicAssessmentAnalytics';
import type { DynamicAssessmentApiClient } from '../types/dynamicAssessmentApiClient';
import type { DynamicAssessmentHost } from '../types/dynamicAssessmentHost';

export type DynamicAssessmentContextValue = {
  apiClient: DynamicAssessmentApiClient;
  host?: DynamicAssessmentHost;
  analytics: ResolvedDynamicAssessmentAnalytics;
  /** Resolved default already applied — see `DynamicAssessmentProviderProps.showContactSupport`. */
  showContactSupport: boolean;
};

/**
 * Module-internal. Consumers read it through the paired hooks in `../hooks/`
 * (`useDynamicAssessmentApiClient`, `useDynamicAssessmentAnalytics`, `useDynamicAssessmentHost`) — this is
 * exported only so those hooks can reach it, and is deliberately absent from the
 * module's public barrel.
 */
export const DynamicAssessmentContext = createContext<DynamicAssessmentContextValue | null>(null);

type DynamicAssessmentProviderProps = {
  /**
   * The HTTP client the dynamic-assessment calls run on. The onboarding app passes
   * its `apiClient`; a host app passes its own already-authenticated instance.
   * Response casing is normalized inside `dynamicAssessments/api`, so the client does not need
   * a camelizing interceptor.
   *
   * **Must be referentially stable** — unlike `analytics`, which this provider
   * defends against inline objects via a ref. `apiClient` reaches
   * `useDynamicAssessmentSession`'s pass effect dependencies directly, so
   * `apiClient={createClient()}` inline re-runs the pass on every render: an
   * infinite request loop, not a slow render. Hoist it to a module singleton or
   * memoize it in the host.
   *
   * Two contracts the type cannot enforce, both of which fail at runtime rather
   * than at compile time — `DynamicAssessmentApiClient` is a structural `get`/`post` shape,
   * so anything with those two methods satisfies it:
   *
   *   - It must send whatever authorization the endpoints expect (a bearer token
   *     today). A client authenticating some other way — a cookie-session client,
   *     say — type-checks and then 401s.
   *   - It must REJECT on a non-2xx response. A client that resolves instead (a
   *     bare `fetch` wrapper) makes the assessment silently report itself complete
   *     rather than erroring; see the note on `DynamicAssessmentApiClient` for the chain.
   *
   * See `docs/packaging-plan.md` Q4.
   */
  apiClient: DynamicAssessmentApiClient;
  /**
   * Required only by `DynamicAssessmentScreen`, which needs flag gating and
   * somewhere to send its terminal outcomes. `DynamicAssessmentFlow` runs without
   * it — a host that just wants to serve the flows can omit it entirely.
   */
  host?: DynamicAssessmentHost;
  /**
   * Where assessment telemetry goes. Optional — omitted members become no-ops, so a
   * host with no interest in assessment analytics can leave this out. The onboarding
   * app must pass it: these events are the dynamic-assessment rollout funnel.
   *
   * Safe to pass inline (`analytics={{ trackEvent, logError }}`) — the provider
   * stabilizes the identities, see below.
   */
  analytics?: DynamicAssessmentAnalytics;
  /**
   * Token for the Transifex project holding the dynamicAssessments module's strings.
   *
   * Supplied rather than read from `process.env`, for two reasons. Inside a
   * consumer's bundle `babel-preset-expo` does not inline `EXPO_PUBLIC_*` across
   * the `node_modules` boundary, so an env read here would be `undefined` no
   * matter how the host configures it. And the module scopes `useT()` to its own
   * Transifex instance (see `../i18n/assessmentTx`), so the token has to reach that
   * instance from outside.
   *
   * Omitting it is safe and explicit: the instance keeps its empty token and
   * the assessment renders source (English) strings. It never falls back to the host's
   * default `tx` — inheriting whichever token a host happened to init last is
   * the failure this prop exists to prevent.
   */
  transifexToken?: string;
  /**
   * Whether question screens render the "Want help? Call us at …" contact-support
   * line (`AssessmentContent`'s trailing `<ContactSupport showPhone />`). Defaults
   * to `true`, which is what the onboarding web app relies on implicitly today.
   *
   * A host app with its own in-app support channel (e.g. VidaReactNative's
   * Zendesk widget) should pass `false` — the assessment screens are embedded
   * there, not the whole onboarding surface, so a second, unrelated phone number
   * for Vida's web support line has no channel of its own to point at.
   */
  showContactSupport?: boolean;
  children: ReactNode;
};

/**
 * The dynamicAssessments module's single injection point.
 *
 * The module imports nothing from `@/modules/rulesEngine`, doesn't reach for the
 * app's `@/modules/api` singleton, and doesn't import `@/modules/analytics` (which
 * would construct a second PostHog client at import). All three arrive here
 * instead — which is what keeps `@/modules/dynamicAssessments` mountable in an app
 * (VidaReactNative) that has its own versions of each.
 *
 * Translations are the fourth: `useT()` binds to whatever `TXProvider` is nearest,
 * or to the process-wide default `tx` if there is none. Scoping it here to the
 * module's own instance is what stops a host's `tx.init` from silently dropping
 * every assessment string to its English source.
 *
 * See `docs/packaging-plan.md` §4 and §9.6.
 */
export function DynamicAssessmentProvider({
  apiClient,
  host,
  analytics,
  transifexToken,
  showContactSupport = true,
  children,
}: DynamicAssessmentProviderProps) {
  const systemLocale = useSystemLocale();
  // The analytics functions end up in hook dependency arrays downstream (the
  // session hook's pass effect among them). A host passing an inline object would
  // otherwise hand us a new identity every render and re-trigger those effects —
  // for the pass effect, that's an infinite request loop. Route the calls through
  // a ref so the exposed identities are stable no matter how the host passes them.
  const analyticsRef = useRef(analytics);

  const trackEvent = useCallback<ResolvedDynamicAssessmentAnalytics['trackEvent']>(
    (eventName, properties) => {
      analyticsRef.current?.trackEvent?.(eventName, properties);
    },
    []
  );

  const logError = useCallback<ResolvedDynamicAssessmentAnalytics['logError']>(
    (message, context) => {
      // Deliberately not awaited or surfaced: telemetry must never break the flow.
      void analyticsRef.current?.logError?.(message, context);
    },
    []
  );

  useEffect(() => {
    analyticsRef.current = analytics;
  }, [analytics]);

  // Configure the token in a layout effect rather than during render.
  // `configureAssessmentTxToken` calls `assessmentTx.init()`, which mutates a module
  // singleton — doing that in render mutates it for renders React then discards
  // (StrictMode's double invoke, an aborted concurrent render).
  //
  // A layout effect still lands before paint, and — the part that actually
  // matters — before any child's `useT()` subscription: `@transifex/react`
  // subscribes with a passive `useEffect`, and a parent's layout effect runs
  // ahead of every passive effect. `init()` is asynchronous regardless, so no
  // arrangement can have translations ready for the first paint; the ordering
  // worth guaranteeing is init-before-subscribe, and this guarantees it.
  useLayoutEffect(() => {
    configureAssessmentTxToken(transifexToken);
  }, [transifexToken]);

  // Follow the device locale on our own instance — `tx.setCurrentLocale` on the
  // default instance wouldn't reach it. No ready gate, unlike the app-level
  // `useLanguageSetting`: blocking a host's render on our CDS fetch isn't ours to
  // do, so a non-English user may see source strings for the moment it takes.
  //
  // English maps to `''`, not to `'en'`. `''` is Transifex's source-language
  // state: `t()` returns the source string and nothing is fetched. `'en'` is NOT
  // the way back — it is the source language, CDS rejects the request (verified:
  // HTTP 403) and `currentLocale` is left as it was. Short-circuiting on `'en'`
  // therefore stranded an instance already on `es_US`, which kept rendering
  // Spanish after the device switched to English. Comparing the target against
  // the instance's own locale is what makes the transition work both ways.
  useEffect(() => {
    const locale = getTransifexLocale(systemLocale);
    const target = locale === 'en' ? '' : locale;
    if (target === assessmentTx.getCurrentLocale()) return;
    assessmentTx.setCurrentLocale(target).catch(() => {
      // Offline or a CDS error — falls through to source strings. Never surfaced:
      // a translation fetch must not be able to fail the assessment.
    });
  }, [systemLocale]);

  // Memoized separately from `value` on purpose. Inlined as `analytics: { … }` it
  // would be rebuilt whenever `apiClient` or `host` changed, so a consumer doing
  // `const analytics = useDynamicAssessmentAnalytics()` and putting the OBJECT in a dep array
  // (rather than destructuring the stable functions) would see it churn. Keyed only
  // on the two stable callbacks, this identity never changes for the provider's
  // lifetime.
  const resolvedAnalytics = useMemo<ResolvedDynamicAssessmentAnalytics>(
    () => ({ trackEvent, logError }),
    [trackEvent, logError]
  );

  // Kept manual rather than left to the React Compiler: `reactCompiler` is on for
  // THIS app (app.json), but this module is consumed by apps that may not enable
  // it — without memoization there, every provider render would churn all consumers.
  const value = useMemo(
    () => ({ apiClient, host, analytics: resolvedAnalytics, showContactSupport }),
    [apiClient, host, resolvedAnalytics, showContactSupport]
  );

  return (
    <DynamicAssessmentContext.Provider value={value}>
      {/* Scopes every `useT()` / `<T>` below to the dynamicAssessments module's instance,
          including the `modules/ui` components it renders. Outside this provider
          those same components still bind to the ambient instance, which is what
          the onboarding app's own screens rely on. */}
      <TXProvider instance={assessmentTx}>{children}</TXProvider>
    </DynamicAssessmentContext.Provider>
  );
}
